import { Box, Grid, Typography } from "@mui/material";
import React, { useMemo } from "react";
import ImageComponent from "../../dsasComponents/ImageComponent.js";
import { useScoreResolver } from "../../hooks/useScoreResolver.js";
import MatchStatusDetail from "../../match/fragments/MatchStatusDetail.js";
import { useMatchStatusResolver } from "../../hooks/useMatchStatusResolver.js";

const SHOOTOUT_GAMEFACT_TYPES = new Set([
    'rugby_shootoutpenalty',
    'soccer_shootoutpenalty',
    'handball_shootout',
]);

const isSuccessValue = (value) =>
    value === true || value === 1 || value === '1';

const isSameId = (left, right) =>
    left != null && right != null && String(left) === String(right);

const resolveShootoutScore = (match) => {
    const gameFacts = Array.isArray(match?.gameFacts) ? match.gameFacts : [];
    const homeTeamId = match?.homeTeam?._id;
    const awayTeamId = match?.awayTeam?._id;

    if (!homeTeamId || !awayTeamId || !gameFacts.length) {
        return {
            homeShootoutScore: 0,
            awayShootoutScore: 0,
            hasShootout: false,
        };
    }

    let homeShootoutScore = 0;
    let awayShootoutScore = 0;
    let hasShootout = false;

    gameFacts.forEach((gameFact) => {
        if (!SHOOTOUT_GAMEFACT_TYPES.has(gameFact?.type)) return;

        const detail = gameFact?.detail || {};
        const teamId = detail.team;

        if (!teamId) return;

        hasShootout = true;

        const isScored =
            isSuccessValue(detail.butSucced) ||
            isSuccessValue(detail.penalitySucced);

        if (!isScored) return;

        if (isSameId(teamId, homeTeamId)) {
            homeShootoutScore += 1;
        } else if (isSameId(teamId, awayTeamId)) {
            awayShootoutScore += 1;
        }
    });

    return {
        homeShootoutScore,
        awayShootoutScore,
        hasShootout,
    };
};

const MatchDisplay = React.memo(({match}) => {

    const {homeTeam, awayTeam} = match

    const [homeScore, awayScore] = useScoreResolver(match)

    const {isMatchEnded} = useMatchStatusResolver(match)

    const {
        homeShootoutScore,
        awayShootoutScore,
        hasShootout,
    } = useMemo(() => resolveShootoutScore(match), [match, match?.gameFacts])

    const isHomeQualified = isMatchEnded && (
        homeScore > awayScore ||
        (homeScore === awayScore && homeShootoutScore > awayShootoutScore)
    )

    const isAwayQualified = isMatchEnded && (
        awayScore > homeScore ||
        (homeScore === awayScore && awayShootoutScore > homeShootoutScore)
    )
    
    return (
      <Box sx={{
                width: '100%',
                '&:hover .match-display__shootout': {
                color: 'white',
                },
            }}>
        <Grid container alignItems="center" minHeight={70}>
          {/* Équipe domicile */}
          <Grid item xs={3.5} textAlign={"center"}>
              <Typography variant={"p"} fontWeight={isHomeQualified ? 'bold' : 'normal'}>{homeTeam.name}</Typography>
          </Grid>
          {/* Score */}
          <Grid item xs={5} sx={{
              display : 'flex',
              flexDirection : 'row',
              justifyContent : 'space-between',
              alignItems : 'center',
              position : 'relative'
          }}>
              <ImageComponent src={homeTeam.logoUrl} alt={homeTeam.name} width={30} height={50} />
              <MatchStatusDetail match={match}/>
              <ImageComponent src={awayTeam.logoUrl} alt={awayTeam.name} width={30} height={50} />
          </Grid>

          {/* Équipe extérieure */}
          <Grid item xs={3.5} textAlign={"center"}>
              <Typography variant={"p"} fontWeight={isAwayQualified ? 'bold' : 'normal'}>{awayTeam.name}</Typography>
          </Grid>
        </Grid>

        {hasShootout && (
          <Typography
              className="match-display__shootout"
              variant="caption"
              component="div"
              textAlign="center"
              sx={{
                  mt: -1,
                  mb: 0.75,
                  color: '#6d6e73',
                  fontSize: 11,
                  fontWeight: 700,
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
              }}
          >
              t.a.b : {homeShootoutScore}-{awayShootoutScore}
          </Typography>
        )}
      </Box>
      );

}, (prevProps, nextProps) => {
    return prevProps.match.gameFacts === nextProps.match.gameFacts && prevProps.match.timer === nextProps.match.timer && prevProps.match.period === nextProps.match.period && prevProps.match.statusTimer === nextProps.match.statusTimer && prevProps.match.status === nextProps.match.status;
});

export default MatchDisplay
